import { useEffect, useState } from "react";

export type AuthStatus = {
  authenticated: boolean;
  user?: string;
  scopes?: string[];
  checked_at: string;
  error?: string;
};

const POLL_MS = 5_000;

// useAuthStatus polls /api/auth/status with a small interval so that
// when the user completes the device-flow elsewhere (or restarts bee
// with a token in env), the SPA picks up the new state and transitions
// out of the login screen. The interval is intentionally low — the
// endpoint is a single GitHub /user call cached behind ghtoken.Resolve.
export function useAuthStatus(): {
  status: AuthStatus | null;
  loading: boolean;
  refresh: () => void;
} {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const res = await fetch("/api/auth/status");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as AuthStatus;
      setStatus(data);
    } catch (err) {
      setStatus({
        authenticated: false,
        checked_at: new Date().toISOString(),
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const id = window.setInterval(fetchStatus, POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  return { status, loading, refresh: fetchStatus };
}
